import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  MapPin, Upload as UploadIcon, Camera, FileText, Pencil, Trash2, Clock, Plus,
  Ruler, Zap, Truck, X, FolderInput,
} from "lucide-react";
import { toast } from "sonner";
import HoursDialog from "@/components/HoursDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const CATEGORIES = [
  { key: "DISEGNI", label: "Disegni", icon: Ruler,  color: "bg-sky-50 text-sky-600",        badge: "bg-sky-600 text-white" },
  { key: "SCHEMI",  label: "Schemi",  icon: Zap,    color: "bg-amber-50 text-amber-600",    badge: "bg-amber-500 text-white" },
  { key: "DDT",     label: "DDT",     icon: Truck,  color: "bg-emerald-50 text-emerald-600", badge: "bg-emerald-600 text-white" },
  { key: "FOTO",    label: "Foto",    icon: Camera, color: "bg-cyan-50 text-cyan-700",       badge: "bg-cyan-600 text-white" },
];

function formatSize(bytes) {
  if (!bytes) return "0 B";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT");
}

function CategoryCard({ cat, count, active, onClick }) {
  const Icon = cat.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`category-card-${cat.key.toLowerCase()}`}
      className={
        "relative p-5 rounded-xl border transition text-center " +
        (active
          ? "brand-navy text-white border-transparent shadow"
          : "bg-white border-slate-200 hover:border-slate-300")
      }
    >
      <div
        className={
          "w-10 h-10 rounded-lg mx-auto flex items-center justify-center " +
          (active ? "bg-white/15 text-white" : cat.color)
        }
      >
        <Icon className="w-5 h-5" />
      </div>
      <div
        className={
          "mt-3 text-[11px] tracking-[0.16em] font-semibold " +
          (active ? "text-white/90" : "text-slate-500")
        }
      >
        {cat.label.toUpperCase()}
      </div>
      <span
        className={
          "absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center " +
          cat.badge
        }
      >
        {count || 0}
      </span>
    </button>
  );
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("FOTO");
  const [files, setFiles] = useState([]);
  const [counts, setCounts] = useState({ DISEGNI: 0, SCHEMI: 0, DDT: 0, FOTO: 0 });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [hours, setHours] = useState([]);
  const [hoursOpen, setHoursOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = async () => {
    try {
      const [jobRes, allFilesRes, hoursRes] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/files`),
        api.get(`/jobs/${id}/hours`),
      ]);
      setJob(jobRes.data);
      const all = allFilesRes.data;
      const c = { DISEGNI: 0, SCHEMI: 0, DDT: 0, FOTO: 0 };
      all.forEach((f) => { if (c[f.category] !== undefined) c[f.category] += 1; });
      setCounts(c);
      setFiles(all);
      setHours(hoursRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const filteredFiles = files.filter((f) => f.category === category);

  const doUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const form = new FormData();
        form.append("category", category);
        form.append("file", f);
        form.append("label", f.name);
        await api.post(`/jobs/${id}/files`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("File caricati");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (fileId) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await api.delete(`/files/${fileId}`);
      toast.success("File eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const onRename = async (f) => {
    const next = window.prompt("Nuovo nome:", f.label || f.original_name);
    if (!next) return;
    try {
      await api.patch(`/files/${f.id}`, { label: next });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const onMove = async (f, newCategory) => {
    if (newCategory === f.category) return;
    try {
      await api.patch(`/files/${f.id}`, { category: newCategory });
      toast.success(`Spostato in ${newCategory}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const onDeleteHour = async (hId) => {
    if (!window.confirm("Eliminare questa registrazione?")) return;
    try {
      await api.delete(`/hours/${hId}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const toggleStatus = async () => {
    if (!job) return;
    const next = job.status === "active" ? "completed" : "active";
    try {
      await api.patch(`/jobs/${id}`, { status: next });
      toast.success(next === "completed" ? "Lavoro concluso" : "Lavoro riattivato");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  if (loading || !job) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar showBack />
        <div className="p-10 text-center text-slate-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-detail-page">
      <Navbar showBack />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header card */}
        <Card className="p-5 sm:p-7 rounded-xl bg-white border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-navy">{job.project_name}</h1>
              <div className="mt-1 text-sm text-slate-600 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-rose-500" />
                {job.site_address}
              </div>
              <div className="mt-1 text-xs text-slate-500">Cliente: {job.client} · Responsabile: {job.manager}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className={
                  "text-xs font-semibold px-3 py-1 rounded-full " +
                  (job.status === "active"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600")
                }
                data-testid="job-status-badge"
              >
                {job.status === "active" ? "Attivo" : "Concluso"}
              </span>
              {isAdmin && (
                <Button
                  size="sm" variant="outline"
                  onClick={toggleStatus}
                  data-testid="toggle-status-button"
                >
                  {job.status === "active" ? "Concludi" : "Riattiva"}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4">
            <div className="border rounded-lg p-4 text-center bg-slate-50/50">
              <div className="text-base sm:text-lg font-bold text-brand-navy">
                {formatDate(job.start_date)}
              </div>
              <div className="text-[10px] sm:text-xs tracking-[0.18em] text-slate-500 mt-1">INIZIO</div>
            </div>
            <div className="border rounded-lg p-4 text-center bg-slate-50/50">
              <div className="text-base sm:text-lg font-bold text-brand-orange">
                {job.total_hours}h
              </div>
              <div className="text-[10px] sm:text-xs tracking-[0.18em] text-slate-500 mt-1">
                ORE TOTALI · {job.total_days} GG
              </div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="files" className="mt-6">
          <TabsList className="grid grid-cols-2 w-full sm:w-auto">
            <TabsTrigger value="files" data-testid="tab-files">Documenti</TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-hours">Ore lavorate</TabsTrigger>
          </TabsList>

          <TabsContent value="files" className="mt-5">
            {/* Categories */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {CATEGORIES.map((c) => (
                <CategoryCard
                  key={c.key}
                  cat={c}
                  count={counts[c.key]}
                  active={category === c.key}
                  onClick={() => setCategory(c.key)}
                />
              ))}
            </div>

            {/* Uploader */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-stretch">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault(); setDragOver(false);
                  doUpload(e.dataTransfer.files);
                }}
                className={
                  "dropzone rounded-xl p-8 text-center flex flex-col items-center justify-center " +
                  (dragOver ? "border-brand-navy bg-blue-50/50" : "")
                }
                data-testid="file-dropzone"
              >
                <UploadIcon className="w-6 h-6 text-brand-navy mb-2" />
                <div className="text-sm text-slate-600">
                  Trascina oppure{" "}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-brand-navy underline font-semibold"
                    data-testid="file-browse-button"
                  >
                    sfoglia
                  </button>
                </div>
                <div className="text-xs text-slate-400 mt-1">{CATEGORIES.find((c) => c.key === category)?.label}</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => doUpload(e.target.files)}
                />
              </div>
              {category === "FOTO" && (
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="brand-navy text-white rounded-xl px-6 flex flex-col items-center justify-center min-w-[120px]"
                  data-testid="camera-button"
                >
                  <Camera className="w-5 h-5 mb-1" />
                  <span className="text-xs font-semibold tracking-wider">FOTO</span>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => doUpload(e.target.files)}
                  />
                </button>
              )}
            </div>

            {uploading && (
              <div className="mt-3 text-sm text-slate-500" data-testid="uploading-indicator">Caricamento in corso...</div>
            )}

            {/* Files list */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 divide-y" data-testid="files-list">
              {filteredFiles.length === 0 && (
                <div className="p-8 text-center text-slate-500 text-sm">
                  Nessun file caricato in questa categoria.
                </div>
              )}
              {filteredFiles.map((f) => (
                <div key={f.id} className="flex items-center gap-3 p-4" data-testid={`file-row-${f.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <a
                    href={`${API_BASE}/uploads/${f.stored_name}`}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1"
                  >
                    <div className="font-semibold text-slate-800 truncate">{f.label || f.original_name}</div>
                    <div className="text-xs text-slate-500">
                      {formatSize(f.size_bytes)} · {formatDate(f.uploaded_at)} · {f.uploaded_by_name}
                    </div>
                  </a>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-2 text-slate-400 hover:text-slate-700"
                        aria-label="Sposta"
                        title="Sposta in un'altra categoria"
                        data-testid={`file-move-${f.id}`}
                      >
                        <FolderInput className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="text-xs text-slate-500">Sposta in</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {CATEGORIES.filter((c) => c.key !== f.category).map((c) => {
                        const Icon = c.icon;
                        return (
                          <DropdownMenuItem
                            key={c.key}
                            onClick={() => onMove(f, c.key)}
                            data-testid={`file-move-to-${c.key.toLowerCase()}-${f.id}`}
                            className="cursor-pointer"
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            {c.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    onClick={() => onRename(f)}
                    className="p-2 text-slate-400 hover:text-slate-700"
                    aria-label="Rinomina"
                    data-testid={`file-rename-${f.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(f.id)}
                    className="p-2 text-rose-400 hover:text-rose-600 bg-rose-50 rounded-md"
                    aria-label="Elimina"
                    data-testid={`file-delete-${f.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="hours" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-slate-600">
                Totale: <span className="font-bold text-brand-orange">{job.total_hours}h</span> · {job.total_days} gg lavorati
              </div>
              <Button onClick={() => setHoursOpen(true)} className="brand-navy text-white" data-testid="add-hours-button">
                <Plus className="w-4 h-4 mr-2" />
                Registra ore
              </Button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="hours-list">
              {hours.length === 0 && (
                <div className="p-8 text-center text-slate-500 text-sm">
                  Nessuna ora registrata.
                </div>
              )}
              {hours.map((h) => (
                <div key={h.id} className="flex items-center gap-3 p-4" data-testid={`hours-row-${h.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800">
                      {formatDate(h.date)} · {h.hours}h
                    </div>
                    <div className="text-xs text-slate-500">
                      {h.user_name}{h.notes ? ` · ${h.notes}` : ""}
                    </div>
                  </div>
                  {(isAdmin || h.user_id === user.id) && (
                    <button
                      onClick={() => onDeleteHour(h.id)}
                      className="p-2 text-rose-400 hover:text-rose-600"
                      aria-label="Elimina"
                      data-testid={`hours-delete-${h.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <HoursDialog
        open={hoursOpen}
        onOpenChange={setHoursOpen}
        jobId={id}
        onSaved={load}
      />
    </div>
  );
}
